// W149 · 2026-05-28
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { COMPANY_INFO } from '@/lib/company-info';
import type { AuthSession } from '@builder/shared';
import { registerRequestSchema, userRoleSchema } from '@builder/shared';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { PublicHeader } from '@/components/public/PublicHeader';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { AnimatedLogo } from '@/components/AnimatedLogo';
import { cn } from '@/lib/cn';
import { TurnstileWidget, type TurnstileHandle } from '@/components/security/TurnstileWidget';

type SignupRole = 'project_manager' | 'worker' | 'foreman' | 'client' | 'broker';

export default function RegisterPage() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  // P1-2 (W151) · default é gerente (project_manager). Antes era 'worker'
  // (pedreiro): gerente que não reparasse criava conta sem o botão "Nova
  // obra". A maioria de quem se cadastra direto é gerente da obra.
  const [role, setRole] = useState<SignupRole>('project_manager');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // P0-A2 (W152) · `pending_approval` is the expected outcome of a self-serve
  // signup (the approval gate is intentional, by design). It is NOT an error —
  // rendering it in the red error strip read as "signup failed" and made lay
  // users re-submit in a loop, creating duplicate accounts. Mirror the calm
  // warn banner LoginPage already uses for the same backend code.
  const [pendingApproval, setPendingApproval] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [searchParams] = useSearchParams();
  const tsRef = useRef<TurnstileHandle>(null);

  // W149 · intent param sets role pre-selection (e.g. /register?intent=broker)
  useEffect(() => {
    const intent = searchParams.get('intent');
    if (intent === 'broker') setRole('broker');
    else if (intent === 'owner' || intent === 'client') setRole('client');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGoogleSuccess = (session: AuthSession) => {
    setSession(session);
    // Mesmo destino do cadastro por email: gerente novo (welcomedAt NULL)
    // vai pro /welcome direto; client pro portal.
    const dest =
      session.user.role === 'client' ? '/owner' :
      !session.user.welcomedAt ? '/welcome' :
      '/projects';
    navigate(dest, { replace: true });
  };

  const onGoogleError = (message: string, code?: string | null) => {
    if (code === 'pending_approval') {
      setPendingApproval(true);
      setErrors({});
      return;
    }
    setPendingApproval(false);
    setErrors({ form: message });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = registerRequestSchema.safeParse({ email, password, fullName, role });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors({
        email: flat.email?.[0] ?? '',
        fullName: flat.fullName?.[0] ?? '',
        password: flat.password?.[0] ?? '',
      });
      return;
    }
    setErrors({});
    setPendingApproval(false);
    setBusy(true);
    try {
      const turnstileToken = (await tsRef.current?.getToken()) ?? '';
      const session = await api.register(parsed.data, { turnstileToken });
      setSession(session);
      // Gerente recém-cadastrado (welcomedAt NULL) vai DIRETO pro /welcome
      // (guia da 1ª obra) em vez de /projects → que reencaminhava pra
      // /welcome de qualquer jeito. Um hop a menos. Client → portal,
      // broker → seu dashboard.
      const dest =
        session.user.role === 'client' ? '/owner' :
        session.user.role === 'broker' ? '/broker?welcome=1' :
        !session.user.welcomedAt ? '/welcome' :
        '/projects';
      navigate(dest, { replace: true });
    } catch (err) {
      tsRef.current?.reset();
      if (err instanceof ApiError && err.code === 'pending_approval') {
        // Expected, calm outcome — not a failure. Show the warn banner.
        setPendingApproval(true);
        setErrors({});
      } else {
        setErrors({
          form:
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : t.auth.couldNotCreate,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  // W149 · broker added to signup options
  const roleOptions = userRoleSchema.options.filter(
    (r): r is SignupRole => r !== 'admin',
  );

  return (
    <div className="relative min-h-screen bg-surface text-ink">
      <PublicHeader />

      <div className="flex min-h-[calc(100vh-56px)] flex-col items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm animate-fade-in-up">
          {/* Hero lockup — single source of truth for mark + wordmark */}
          <div className="mb-8 flex justify-center">
            <AnimatedLogo variant="hero" label={t.common.appName} repeatMs={30_000} />
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-line bg-surface-2 p-6 shadow-xs dark:shadow-xs-dark">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">
              {t.auth.registerTitle}
            </h1>
            <p className="mt-1.5 text-sm text-ink-muted">{t.auth.registerSubtitle}</p>

            <div className="mt-6 space-y-4">
              {/* Role grid — lives above the Google button because the chosen role
                  becomes the role of the new Google-created account. For
                  returning users the backend ignores it. */}
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-ink">{t.auth.iAmA}</span>
                <div className="grid grid-cols-2 gap-2">
                  {roleOptions.map((r) => {
                    const active = role === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={cn(
                          'pressable rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors duration-160 ease-out-soft',
                          active
                            ? 'border-ink bg-ink text-surface'
                            : 'border-line bg-surface-2 text-ink-muted hover:text-ink hover:border-line-strong',
                        )}
                      >
                        {t.members.roles[r]}
                      </button>
                    );
                  })}
                </div>
              </label>

              <GoogleSignInButton
                role={role}
                onSuccess={onGoogleSuccess}
                onError={onGoogleError}
              />

              {pendingApproval && (
                <div
                  role="status"
                  className="animate-fade-in-up rounded-lg border border-warn/30 bg-warn/10 px-3 py-3 text-sm"
                >
                  <p className="font-medium text-ink">
                    {t.wave33.register.pendingTitle}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {t.wave33.register.pendingBody}
                  </p>
                </div>
              )}

              {errors.form && !pendingApproval && (
                <div
                  role="alert"
                  className="animate-fade-in-up rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err"
                >
                  {errors.form}
                </div>
              )}

              {!showEmail ? (
                <button
                  type="button"
                  onClick={() => setShowEmail(true)}
                  className="pressable inline-flex min-h-11 w-full items-center justify-center px-2 text-center text-sm font-medium text-ink-muted transition-colors hover:text-ink"
                >
                  {t.auth.orContinueWithEmail}
                </button>
              ) : (
                <>
                  <div
                    className="flex items-center gap-3 text-xs uppercase tracking-wide text-ink-subtle"
                    aria-hidden="true"
                  >
                    <span className="h-px flex-1 bg-line" />
                    <span>{t.common.or}</span>
                    <span className="h-px flex-1 bg-line" />
                  </div>

                  <form onSubmit={onSubmit} className="animate-fade-in-up space-y-4" noValidate>
                    <Input
                      label={t.auth.fullName}
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.currentTarget.value)}
                      error={errors.fullName}
                      required
                    />
                    <Input
                      label={t.auth.email}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      error={errors.email}
                      required
                    />
                    <Input
                      label={t.auth.password}
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      hint={t.auth.passwordHint}
                      error={errors.password}
                      required
                    />
                    <TurnstileWidget ref={tsRef} action="auth_register" />
                    {/* W149 · aceite obrigatório de termos */}
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        required
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.currentTarget.checked)}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-line accent-ink"
                      />
                      <span className="text-ink-muted leading-snug">
                        {isEn ? (
                          <>
                            I have read and agree to the{' '}
                            <Link to="/termos-de-uso" target="_blank" className="underline text-ink">terms of use</Link>
                            {' '}and{' '}
                            <Link to="/politica-de-privacidade" target="_blank" className="underline text-ink">privacy policy</Link>
                            .
                          </>
                        ) : (
                          <>
                            Li e aceito os{' '}
                            <Link to="/termos-de-uso" target="_blank" className="underline text-ink">termos de uso</Link>
                            {' '}e a{' '}
                            <Link to="/politica-de-privacidade" target="_blank" className="underline text-ink">política de privacidade</Link>
                            .
                          </>
                        )}
                      </span>
                    </label>
                    <Button
                      type="submit"
                      fullWidth
                      size="lg"
                      loading={busy}
                      disabled={!acceptedTerms}
                    >
                      {t.auth.registerCta}
                    </Button>
                  </form>
                </>
              )}
            </div>

            <p className="mt-5 text-center text-sm text-ink-muted">
              <Link
                to="/login"
                className="font-medium text-ink underline-offset-4 hover:underline"
              >
                {t.auth.haveAccount}
              </Link>
            </p>
          </div>
          {/* W149 · compliance footer */}
          <footer className="mt-6 text-center text-xs text-ink-muted">
            <Link to="/termos-de-uso" className="underline">Termos de uso</Link>
            {' · '}
            <Link to="/politica-de-privacidade" className="underline">Política de privacidade</Link>
            {' · '}
            <span>{COMPANY_INFO.razaoSocial}</span>
          </footer>
        </div>
      </div>
    </div>
  );
}
